import { defineComponent, ref, computed, openBlock, createElementBlock, normalizeClass, createElementVNode, toDisplayString } from 'vue';

const TextRoot = [
// Layout
'inline-flex',
// Box
'max-w-full',
// Typography
'whitespace-nowrap'];
const MainText = [
// Layout
'overflow-hidden',
// Typography
'text-ellipsis'];
const RestText = [
// Layout
'shrink'];

/**
 * Use `OrText` component to crop un-wrapped single-line text with ellipsis inside narrow containers.
 * Ellipsis can be placed in the end or in the middle of the text. Convenient for displaying long labels,
 * that have same beginning and differ in the ending part.
 */
var script = defineComponent({
  name: 'OrText',
  props: {
    /**
     * Text to crop.
     */
    text: {
      type: String,
      required: true
    },
    /**
     * The index counted from the end of the string where ellipsis will be placed.
     * Defines how many ending characters will remain visible.
     * By default, ellipsis is placed in the end of the line.
     *
     * Note: Negative values behave same as corresponding positives still counting from the end of the line.
     */
    sliceIndex: {
      type: Number,
      default: 0
    }
  },
  emits: ['focus', 'click'],
  expose: ['root'],
  setup(props, {
    emit
  }) {
    // Refs
    const root = ref();
    // Styles
    const rootStyles = computed(() => ['or-text-v3', ...TextRoot]);
    const mainTextStyles = computed(() => [...MainText]);
    const restTextStyles = computed(() => [...RestText]);
    /**
     * Preserves whitespace at the first position as <code>&nbsp;</code>
     * @param str A string to escape
     */
    const escapeFirst = str => str && '\xa0' + str.slice(1);
    /**
     * Preserves whitespace at the last position as <code>&nbsp;</code>
     * @param str A string to escape
     */
    const escapeLast = str => str && str.slice(0, -1) + '\xa0';
    // State
    const mainText = computed(() => {
      if (props.sliceIndex && props.sliceIndex < props.text.length) {
        let mainPart = props.text.slice(0, Math.abs(props.sliceIndex) * -1);
        if (mainPart[mainPart.length - 1] === ' ') {
          mainPart = escapeLast(mainPart);
        }
        return mainPart;
      }
      return props.text;
    });
    const restText = computed(() => {
      let restPart = props.text.slice(mainText.value.length);
      if (restPart[0] === ' ') {
        restPart = escapeFirst(restPart);
      }
      return restPart;
    });
    // Methods
    function onFocus(event) {
      emit('focus', event);
    }
    function onClick(event) {
      emit('click', event);
    }
    return {
      root,
      rootStyles,
      mainTextStyles,
      restTextStyles,
      mainText,
      restText,
      onFocus,
      onClick
    };
  }
});

function render(_ctx, _cache, $props, $setup, $data, $options) {
  return openBlock(), createElementBlock("div", {
    ref: 'root',
    class: normalizeClass(_ctx.rootStyles),
    onFocus: _cache[0] || (_cache[0] = (...args) => _ctx.onFocus && _ctx.onFocus(...args)),
    onClick: _cache[1] || (_cache[1] = (...args) => _ctx.onClick && _ctx.onClick(...args))
  }, [createElementVNode("div", {
    class: normalizeClass(_ctx.mainTextStyles)
  }, toDisplayString(_ctx.mainText), 3 /* TEXT, CLASS */), createElementVNode("div", {
    class: normalizeClass(_ctx.restTextStyles)
  }, toDisplayString(_ctx.restText), 3 /* TEXT, CLASS */)], 34 /* CLASS, NEED_HYDRATION */);
}

script.render = render;
script.__file = "src/components/or-text-v3/OrText.vue";

export { script as OrTextV3 };
